import React, { useMemo, memo, useEffect, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { Divider } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTracker } from '@dpdgroupuk/react-event-tracker';

import DrawerToggle from './DrawerToggle';
import {
  filterRoutes,
  FilterTypes,
  sortRoutesBySectionRoute,
} from '../../../../models/route';
import { RouteListTabs } from '../../../../enums/RouteListTabs';

import {
  getDefaultRoutes,
  getDriverRoutes,
  getRoutesList,
  getSectionRoute,
} from '../../../../store/selectors/routes';
import { paramsToArray } from '../../../../utils/query';
import { ROUTES_TABS } from '../../../../constants/analytics';
import ListWrapper from './ListWrapper';
import palette from '../../../styles/palette';
import ListView from './ListView';

const useStyles = makeStyles(() => ({
  headerDivider: {
    background: palette.typography.white.neutral,
  },
}));

const RouteList = ({
  routeId,
  sectionId,
  isRouteDetail,
  onRouteCodeClick,
  filters,
  onStatusChange,
  onListAddItemClick,
  onLockChange,
  onOpenLockRoutePopover,
}) => {
  const styles = useStyles();
  const tracker = useTracker();
  const routes = useSelector(getRoutesList);
  const sectionRoute = useSelector((state) =>
    getSectionRoute(state, sectionId)
  );
  const [selectedTab, setSelectedTab] = useState(RouteListTabs.defRoutes);

  const defaultRoutes = useSelector((state) =>
    getDefaultRoutes(state, sectionId)
  );
  const driverRoutes = useSelector(getDriverRoutes);

  useEffect(() => {
    isRouteDetail && setSelectedTab(RouteListTabs.detailed);
  }, [isRouteDetail]);

  const filter = useMemo(
    () => paramsToArray(filters, Object.values(FilterTypes)),
    [filters]
  );

  const currentRoutes = useMemo(() => {
    if (isRouteDetail) {
      const selectedRoute = routes[routeId];
      return selectedRoute ? [selectedRoute] : [];
    }
    return filterRoutes(filter, driverRoutes);
  }, [isRouteDetail, filter, driverRoutes, routes, routeId]);

  const data = useMemo(() => {
    if (selectedTab === RouteListTabs.defRoutes) {
      return sectionRoute ? [sectionRoute, ...defaultRoutes] : defaultRoutes;
    } else if (isRouteDetail) {
      return currentRoutes;
    } else {
      const routesData = sectionRoute
        ? [sectionRoute, ...currentRoutes, ...defaultRoutes]
        : [...currentRoutes, ...defaultRoutes];
      return sortRoutesBySectionRoute(routesData);
    }
  }, [selectedTab, isRouteDetail, currentRoutes, defaultRoutes, sectionRoute]);

  const onTabChange = useCallback(
    (e, value) => {
      if (e) {
        setSelectedTab(value);
        tracker.logEvent(ROUTES_TABS[value]);
      }
    },
    [setSelectedTab, tracker]
  );

  return (
    <>
      <DrawerToggle
        onListAddItemClick={onListAddItemClick}
        value={selectedTab}
        onChange={onTabChange}
        isRouteDetail={isRouteDetail}
      />
      <Divider className={styles.headerDivider} />
      <ListWrapper>
        <ListView
          selectedTab={selectedTab}
          routes={data}
          selectedRoute={routeId}
          onRouteCodeClick={onRouteCodeClick}
          onStatusChange={onStatusChange}
          onLockChange={onLockChange}
          onOpenLockRoutePopover={onOpenLockRoutePopover}
        />
      </ListWrapper>
    </>
  );
};

export default memo(RouteList);
